import { useCallback, useMemo, useRef, useState } from "react";

import type { Options, PointOptionsObject } from "highcharts";
import { renderToStaticMarkup } from "react-dom/server";

import {
    LineGraph,
    LEGEND_POSITIONS,
    formatDate,
    shortMonthAndShortYear,
    shortMonthAndYearWithDelimiter,
} from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { type ColumnDefinition, ChartTooltip, SegmentedControl } from "@similarweb/ui-components";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import {
    getGoogleAiModeAnnotationBanner,
    getGoogleAiModeAnnotationPlotLines,
    isGoogleAiModeAffectedChannel,
} from "pro-features/components/Chart/src/configs/plotLines/googleAiModeAnnotations";

import type { ICategoryComparison } from "../../categories-context/CategoriesContext";

import { exportMarketingChannelsComparisonPng } from "./TrafficVolumesLineChart/utils/useTrafficVolumesChartPngExport";
import type { MarketingChannelsData } from "./useMarketingChannelsQueries";

const formatNumber: (value: string | number) => string = abbrNumberFilter();

interface ITooltipDataRecord {
    value: number;
    custom: {
        label: string;
        color: string;
    };
}

const tooltipColumnDefinition: Array<ColumnDefinition<ITooltipDataRecord>> = [
    {
        key: "custom",
        get title() {
            return translate("market_analysis.marketing_channels.channel_traffic.chart_tooltip.industry");
        },
        align: "left",
        selector: "custom",
        size: "200px",
        render: ({ custom }) => (
            <div data-automation="chart-tooltip-label">
                <ChartTooltip.LabelCell
                    label={custom.label}
                    backgroundColor={custom.color}
                />
            </div>
        ),
    },
    {
        key: "value",
        get title() {
            return translate("market_analysis.marketing_channels.channel_traffic.chart_tooltip.traffic");
        },
        align: "right",
        selector: "value",
        size: ChartTooltip.ValueCellDefaultwidth,
        render: ({ value }) => (
            <div data-automation="chart-tooltip-value">
                <ChartTooltip.ValueCell value={formatNumber(value)} />
            </div>
        ),
    },
];

type CategoryId = string;

interface IMarketingChannelsChannelTrafficWidgetProps {
    isLoading: boolean;
    isError: boolean;
    data: Record<CategoryId, MarketingChannelsData | null>;
    categories: ICategoryComparison[];
}

export const MarketingChannelsChannelTrafficWidget = ({
    isLoading,
    isError,
    data,
    categories,
}: IMarketingChannelsChannelTrafficWidgetProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");
    const [selectedChannelIndex, setSelectedChannelIndex] = useState<number>(0);
    const [legendVisibility, setLegendVisibility] = useState<Record<string, boolean>>({});

    const handleExportChartPng = useCallback(() => {
        return exportMarketingChannelsComparisonPng(containerRef, categories);
    }, [containerRef, categories]);

    const channelMapping = useMemo(() => {
        if (!data || isError) {
            return new Map<string, string>();
        }

        const mapping = new Map<string, string>();
        Object.values(data).forEach((categoryData) => {
            if (categoryData?.channelVolumeList) {
                categoryData.channelVolumeList.forEach((channelVolume) => {
                    const translatedName = translate(channelVolume.titleKey);
                    const rawChannelName = channelVolume.channel;
                    mapping.set(translatedName, rawChannelName);
                });
            }
        });

        return mapping;
    }, [data, isError]);

    const availableChannels: string[] = useMemo(() => {
        if (!data || isError) {
            return [];
        }

        const allChannels = new Set<string>();
        Object.values(data).forEach((categoryData) => {
            if (categoryData?.channelVolumeList) {
                categoryData.channelVolumeList.forEach((channelVolume) => {
                    allChannels.add(translate(channelVolume.titleKey));
                });
            }
        });

        return Array.from(allChannels);
    }, [data, isError]);

    const seriesData = useMemo(() => {
        if (!data || isError) {
            return [];
        }

        const selectedChannel = availableChannels[selectedChannelIndex];
        if (!selectedChannel) {
            return [];
        }

        const rawChannelName = channelMapping.get(selectedChannel);
        if (!rawChannelName) {
            return [];
        }

        return categories.map((category) => {
            const categoryData = data[category.id];

            const chartData: PointOptionsObject[] = [];

            if (categoryData?.graph?.[rawChannelName]) {
                const timeSeriesData = categoryData.graph[rawChannelName];
                if (timeSeriesData && typeof timeSeriesData === "object") {
                    Object.entries(timeSeriesData).forEach(([date, value]) => {
                        chartData.push({
                            x: date,
                            y: typeof value === "number" ? value : 0,
                            custom: {
                                label: category.category.text ?? category.category.name,
                                color: category.color,
                            },
                        });
                    });
                }
            }

            return {
                id: category.id,
                color: category.color,
                name: category.category.text ?? category.category.name,
                visible: legendVisibility[category.id] !== false,
                isDisabled: false,
                data: chartData,
                total: categoryData?.total ?? 0,
            };
        });
    }, [data, categories, isError, availableChannels, selectedChannelIndex, channelMapping, legendVisibility]);

    const isAnnotatedChannel = isGoogleAiModeAffectedChannel(
        channelMapping.get(availableChannels[selectedChannelIndex]),
    );

    const displayedPeriods = useMemo(
        () => Array.from(new Set(seriesData.flatMap((series) => series.data.map(({ x }) => x)))),
        [seriesData],
    );

    const chartConfig = useMemo(() => {
        return {
            xAxis: {
                plotLines: isAnnotatedChannel ? getGoogleAiModeAnnotationPlotLines(displayedPeriods) : [],
                labels: {
                    formatter() {
                        return formatDate(this.value, shortMonthAndShortYear);
                    },
                },
            },
            yAxis: {
                labels: {
                    formatter() {
                        return formatNumber(this.value);
                    },
                },
            },
            tooltip: {
                padding: 0,
                useHTML: true,
                outside: false,
                formatter() {
                    const title = formatDate(this.x, shortMonthAndYearWithDelimiter);
                    const tooltipData: { value: number; custom: { label: string; color: string } }[] = this.points.map(
                        ({ options }) => ({
                            value: options.y,
                            custom: options.custom as { label: string; color: string },
                        }),
                    );

                    return renderToStaticMarkup(
                        <Box
                            padding="16px"
                            borderRadius="8px"
                            background={colorsPalettes.carbon[0]}
                        >
                            <ChartTooltip
                                title={title}
                                columnDefinition={tooltipColumnDefinition}
                                data={tooltipData}
                            />
                            {isAnnotatedChannel ? getGoogleAiModeAnnotationBanner(this.x) : null}
                        </Box>,
                    );
                },
            },
        } satisfies Options;
    }, [displayedPeriods, isAnnotatedChannel]);

    const handleChannelChange = useCallback((selectedIndex: number) => {
        setSelectedChannelIndex(selectedIndex);
    }, []);

    const handleLegendToggle = useCallback(
        (filteredData: typeof seriesData) => {
            const newVisibility: Record<string, boolean> = {};

            categories.forEach((category) => {
                const isVisible = filteredData.some((item) => item.id === category.id);
                newVisibility[category.id] = isVisible;
            });

            setLegendVisibility(newVisibility);
        },
        [categories],
    );

    return (
        <Box ref={containerRef}>
            <WidgetContainer
                withBorder
                automationId="marketing-channels-channel-traffic-widget"
                title={translate("market_analysis.marketing_channels.channel_traffic.title")}
                tooltip={translate("market_analysis.marketing_channels.channel_traffic.tooltip")}
                isLoading={isLoading}
                loaderHeight={500}
                loaderWidth="100%"
                noData={isError || seriesData.length === 0}
                subtitleFilters={subtitleFilters}
                onDownloadClick={handleExportChartPng}
                downloadProps={{ PNG: true }}
            >
                <Stack
                    padding="24px"
                    gap="24px"
                >
                    <Flex
                        gap="12px"
                        alignItems="center"
                    >
                        <Text fontVariant="body2">
                            {translate("market_analysis.marketing_channels.channel_traffic.channels.title")}
                        </Text>
                        <SegmentedControl
                            selectedIndex={selectedChannelIndex}
                            values={availableChannels}
                            onChange={handleChannelChange}
                        />
                    </Flex>
                    <LineGraph
                        // keyed on channel: Highcharts keeps first-render plot lines on update
                        key={availableChannels[selectedChannelIndex]}
                        withMarkers
                        isControlledGraph
                        shouldPresentValueAtLegend
                        labelsAreRotated={false}
                        withoutTooltipsPadding={false}
                        graphConfig={chartConfig}
                        data={seriesData}
                        legendPosition={LEGEND_POSITIONS.RIGHT}
                        legendHeaders={[
                            translate("market_analysis.marketing_channels.channel_traffic.chart_legend.industry"),
                            translate("market_analysis.marketing_channels.channel_traffic.chart_tooltip.traffic"),
                        ]}
                        legendValueFilter={formatNumber}
                        legendsMinWidth={250}
                        legendsWithValueMaxWidth={160}
                        graphLegendPaddingLeft={24}
                        legendToggleHandlerCallback={handleLegendToggle}
                    />
                </Stack>
            </WidgetContainer>
        </Box>
    );
};
